import type { CatalogInventoryDetail } from '@skupreme/api-contracts/core';
import { HintLabel } from '@skupreme/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { CommonUtility } from '@utility/common';
import ItemBundlesService from '@utility/services/bundle';
import { Fragment } from 'react';
import { catalogKeys } from '../../lib/query-keys';

type InventoryItem = {
  _id: string;
  p_sku?: string;
  is_bundle?: boolean;
  total_inventory?: number;
  inventoryDetails?: CatalogInventoryDetail[];
};

type Location = Pick<
  CatalogInventoryDetail,
  'fulfillment_provider_id' | 'fulfillment_provider_name' | 'available'
>;

const count = (value: number) => CommonUtility.numberWithCommas(value);
const sum = (rows: Location[]) =>
  rows.reduce((total, row) => total + Math.max(row.available, 0), 0);

/* One group of locations: its title, each location's units, and the total. */
function LocationGroup({ title, rows }: { title: string; rows: Location[] }) {
  if (!rows.length) return null;

  return (
    <div className="sk:flex sk:flex-col sk:gap-1">
      <span className="sk:text-xs sk:font-medium sk:text-fg-tertiary">
        {title}
      </span>
      <dl className="sk:m-0 sk:grid sk:grid-cols-[1fr_auto] sk:gap-x-4 sk:gap-y-1 sk:text-dense">
        {rows.map(row => (
          <Fragment key={row.fulfillment_provider_id}>
            <dt className="sk:text-fg-secondary">
              {row.fulfillment_provider_name}
            </dt>
            <dd className="sk:m-0 sk:text-right sk:tabular-nums">
              {count(row.available)}
            </dd>
          </Fragment>
        ))}
        <dt className="sk:font-medium">Total</dt>
        <dd className="sk:m-0 sk:text-right sk:font-medium sk:tabular-nums">
          {count(sum(rows))}
        </dd>
      </dl>
    </div>
  );
}

/**
 * Available units, and where they sit on hover: ready for
 * fulfillment, in storage, and for a kit what its components could assemble.
 */
export function InventoryCell({ item }: { item: InventoryItem }) {
  const total = item.total_inventory ?? 0;

  const bundle = useQuery({
    queryKey: [...catalogKeys.all, 'bundle-by-item', item._id],
    queryFn: async () =>
      ((await ItemBundlesService.byItem(item._id))?.data?.[0] ?? null) as {
        inventoryDetails?: Location[];
      } | null,
    enabled: !!item.is_bundle,
  });

  const details = item.inventoryDetails ?? [];
  const ready = details.filter(row => !row.is_warehouse && row.is_fulfillable);
  const storage = details.filter(row => row.is_warehouse);
  const virtual = (bundle.data?.inventoryDetails ?? []).filter(
    row => row.available > 0,
  );
  const virtualTotal = sum(virtual);

  if (!total && !item.is_bundle) {
    return <span className="sk:tabular-nums">0</span>;
  }

  return (
    <HintLabel
      placement="bottom start"
      className="sk:whitespace-nowrap sk:tabular-nums"
      hint={
        <div className="sk:flex sk:min-w-56 sk:flex-col sk:gap-3">
          <LocationGroup title="Ready for fulfillment" rows={ready} />
          <LocationGroup title="Kit components (virtual)" rows={virtual} />
          <LocationGroup title="In storage" rows={storage} />
          {!ready.length && !virtual.length && !storage.length ? (
            <span className="sk:text-dense sk:text-fg-tertiary">
              No locations hold this item.
            </span>
          ) : null}
        </div>
      }
    >
      {total || !virtualTotal ? count(total) : null}
      {virtualTotal ? (
        <span className="sk:text-fg-tertiary">
          ({count(virtualTotal)} virtual)
        </span>
      ) : null}
    </HintLabel>
  );
}
